/* eslint-disable complexity */
/* eslint-disable max-lines */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Optional,
  Output,
  Self,
  SimpleChanges
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControlName,
  NgControl
} from '@angular/forms';
import { HelpModal, UlItem } from '../../../models';
import { ControlType } from './../types/form.types';
import { AtomEvent } from './models/input.interface';

@Component({
  selector: 'idp-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss']
})
export class InputComponent implements ControlValueAccessor, OnChanges {
  @Input() addMargin = false;
  @Input() hasLink = false;
  @Input() addtlMaskInfo = false;
  @Input() ariaLabel = '';
  @Input() arrowPosition = 'right';
  @Input() defaultValue = '';
  @Input() disabled = false;
  @Input() disableFocus = false;
  @Input() error = false;
  @Input() errorMessage = '';
  @Input() errorMessages: { [key: string]: string } = {};
  @Input() floatingLabel = true;
  @Input() hideBottomBorder = false;
  @Input() id = '';
  @Input() infoToolTip = false;
  @Input() infoToolTipContent = '';
  @Input() toolTipAriaRole = 'button';
  @Input() trimInput = false;
  @Input() labelTxt = '';
  @Input() name = 'textfield';
  @Input() bottomLabelText = '';
  @Input() multipleErrors = false;
  @Input() maxLength: string | null = null;
  @Input() placeholderText = '';
  @Input() labelIcon = false;
  @Input() type: ControlType = 'text';
  @Input() eyeIcon = false;
  @Input() required = false;
  @Input() showFormGrouped = true;
  @Input() submitAttempt = false;
  @Input() invalidAriaRequired = true;
  @Input() ariaRequired = true;
  @Input() description = false;
  @Input() descriptionMessage = '';
  @Input() helpModal: HelpModal = {
    icon: false,
    title: '',
    content: '',
    closeLabel: '',
    type: '',
    tooltip: false,
    modalStyle: 'slim',
    closeOnBackgroundClick: false,
    hideCloseButton: false
  };

  @Input() labelHelpIcon = '';
  @Input() labelSrOnly = false;
  @Input() closeEscInfoToolTip = false;
  @Input() strictNumbers = false;
  @Input() autocomplete = 'off';
  @Output() focusOut: EventEmitter<UlItem> = new EventEmitter<UlItem>();
  @Output() isFocused: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() value: EventEmitter<{
    id: string;
    value: string;
    codeKey: string | number;
  }> = new EventEmitter();

  @Output() wasTabbed: EventEmitter<{
    id: string;
    value: string;
    target: HTMLInputElement;
  }> = new EventEmitter();

  onChange!: (item: { id: string; value: string } | null) => void;
  onTouched!: () => void;
  focused = false;
  showPassword = false;
  validElement: boolean | null = false;
  toolTipClickContent = false;

  constructor(
    @Optional() @Self() private ngControl: NgControl,
    @Optional() private controlName: FormControlName,
    private elementRef: ElementRef
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get errorState() {
    if (
      this.ngControl &&
      this.ngControl.errors &&
      this.ngControl.touched &&
      this.submitAttempt &&
      Object.keys(this.errorMessages).length
    ) {
      this.errorMessage =
        this.errorMessages[Object.keys(this.ngControl.errors)[0]];
    }

    return (
      this.ngControl &&
      this.ngControl.errors &&
      this.ngControl.touched &&
      this.submitAttempt
    );
  }

  shouldDisable(event: KeyboardEvent): boolean {
    const key: string = event.key;
    return key === 'Tab' ? false : this.disabled || this.disableFocus;
  }

  autoFillDetectChange(event: Event) {
    this.setValue(event as KeyboardEvent);
  }

  setValue(event: KeyboardEvent): void {
    this.emitValue(event);
    const eventTarget = event.target as HTMLInputElement;
    if (eventTarget?.value && eventTarget.value.trim() !== '') {
      this.validElement = true;
    } else {
      this.validElement = null;
    }
    if (eventTarget.value.trim() === '') {
      if (this.onChange) {
        this.onChange(null);
      }
    } else {
      if (this.onChange) {
        this.onChange({
          id: this.id,
          value: this.trimText(this.trimInput, eventTarget.value)
        });
      }
    }
  }

  setValuePaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (event.type === 'paste') {
      this.emitValuePaste(event);
    }
    if (
      clipboardData?.getData('Text') &&
      clipboardData.getData('Text').trim() !== ''
    ) {
      this.validElement = true;
    } else {
      this.validElement = null;
    }
    if (event.clipboardData?.getData('Text') !== '') {
      const clipboardText = event.clipboardData?.getData('Text') ?? '';
      this.onChange({
        id: this.id,
        value: this.trimText(this.trimInput, clipboardText)
      });
    } else {
      this.onChange(null);
    }
    return;
  }

  emitValue(event: KeyboardEvent): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const key: string | number = event.key;
    this.value.emit({
      id: this.id,
      value: this.trimText(this.trimInput, target.value),
      codeKey: key
    });
  }

  emitTabKeyHit(event: Event) {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    this.wasTabbed.emit({
      id: this.id,
      value: this.trimText(this.trimInput, target.value),
      target
    });
  }

  emitValuePaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    const key: string = event.type;
    const value: string = clipboardData?.getData('Text') ?? '';
    this.value.emit({
      id: this.id,
      value,
      codeKey: key
    });
    return;
  }

  togglePassword(event: Event) {
    const keyboardEvent = event as KeyboardEvent;

    if (
      keyboardEvent.code === 'Space' ||
      keyboardEvent.code === 'Enter' ||
      keyboardEvent.type === 'click'
    ) {
      keyboardEvent.preventDefault();
      this.showPassword = !this.showPassword;
      this.type = this.showPassword ? 'text' : 'password';
    }
  }

  setFocused(value: boolean, event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (this.onTouched) {
      this.onTouched();
    }
    const eventTarget = keyboardEvent.target as HTMLInputElement;
    if (eventTarget?.value && eventTarget?.value.trim() !== '') {
      this.validElement = true;
    } else if (this.type === 'number') {
      this.validElement = true;
    } else {
      this.validElement = null;
    }
    this.focused = value;
    this.isFocused.emit(value);
  }

  setFocusOut(event: Event) {
    const target = event.target as HTMLInputElement;
    const key: string = event.type;
    this.defaultValue = this.trimInput
      ? this.defaultValue.trim()
      : this.defaultValue;
    const payload = {
      id: this.id,
      value: this.trimText(this.trimInput, target.value),
      codeKey: key
    };
    this.focusOut.emit(payload);
  }

  writeValue(obj: AtomEvent) {
    if (obj === null) {
      this.validElement = null;
      this.defaultValue = '';
    } else {
      // this.defaultValue =
      //   obj && obj.hasOwnProperty('value') ? obj.value : this.defaultValue;
      this.defaultValue =
        this.trimText(this.trimInput, obj?.value) || this.defaultValue;
      this.validElement = true;
    }
  }

  registerOnChange(fn: (item: { id: string; value: string } | null) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  /** The  parameter <event: Event> has been removed since it is not used in the function */
  iconToolTipClick() {
    this.toolTipClickContent = true;
    setTimeout(() => {
      const element = this.elementRef.nativeElement.querySelector(
        '.idp-error-tooltip__wrapper'
      );
      if (element) {
        element.focus();
      }
    });
  }

  /** First parameter <event: Keyboard> has been removed since it is not used in the function */
  closetoolTipClickContent(type: string) {
    this.toolTipClickContent = false;
    const element = this.elementRef.nativeElement.querySelector(
      '.idp-icon-info-anchor'
    );
    if (element && type === 'close') {
      element.focus();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['defaultValue']) {
      if (
        changes['defaultValue'].currentValue &&
        changes['defaultValue'].currentValue.trim() !== ''
      ) {
        this.validElement = true;
      } else {
        this.validElement = null;
      }
    }
  }

  trimText(trim: boolean, text: string): string {
    return trim && text && text.trim().length > 0 ? text.trim() : text;
  }

  handleInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (this.strictNumbers) {
      inputElement.value = inputElement.value.replace(/[^0-9]/g, '');
    }
  }
}
